import React, { useEffect, useState, useCallback } from "react";
import { api, formatIDR } from "@/lib/api";
import { useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import { useConfig } from "@/contexts/ConfigContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { TrendingUp } from "lucide-react";
import { toast } from "sonner";
import ExpenseLedger from "@/components/ExpenseLedger";

const QUICK_ACCOUNTS = {
  "Tenaga Ahli": "5-50500",
  "Survei & Perjalanan": "6-60002",
  "Dokumen & Cetak": "6-60206",
  Subkontraktor: "5-50501",
  Overhead: "6-60300",
};

export const PnlPanel = ({ deal, onSaved }) => {
  const { t } = useLang();
  const { user } = useAuth();
  const cfg = useConfig();
  const canWrite = user?.role === "admin" || user?.role === "editor";

  const [summary, setSummary] = useState(null);
  const [taxPercent, setTaxPercent] = useState(0);
  const [saving, setSaving] = useState(false);
  const [quickAccount, setQuickAccount] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/deals/${deal.id}/pnl`);
      setSummary(res.data.summary);
      setTaxPercent(res.data.pnl.tax_percent || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    }
  }, [deal.id]);
  useEffect(() => { load(); }, [load]);

  const refresh = async () => {
    await load();
    try {
      const res = await api.get(`/deals/${deal.id}`);
      onSaved?.(res.data);
    } catch { /* deal refresh is best effort */ }
  };

  const saveTax = async () => {
    setSaving(true);
    try {
      const res = await api.put(`/deals/${deal.id}/pnl`, {
        use_deal_value: true, revenue: null, tax_percent: Number(taxPercent) || 0,
      });
      setSummary(res.data.pnl_summary);
      toast.success(t("pnl_saved"));
      onSaved?.(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Error");
    } finally {
      setSaving(false);
    }
  };

  if (!summary) return <div className="col-span-2 text-xs text-slate-400">Loading...</div>;

  const cards = [
    { k: "revenue", v: summary.revenue, tone: "text-slate-900" },
    { k: "total_expense", v: summary.total_expense, tone: "text-rose-600" },
    { k: "net_profit", v: summary.net_profit, tone: summary.net_profit >= 0 ? "text-emerald-600" : "text-rose-600" },
  ];

  return (
    <div className="col-span-2 space-y-4" data-testid="pnl-panel">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        {cards.map((x) => (
          <div key={x.k} className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`pnl-kpi-${x.k}`}>
            <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t(x.k)}</div>
            <div className={`mt-1 text-sm font-bold mono ${x.tone}`}>{formatIDR(x.v)}</div>
          </div>
        ))}
        <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid="pnl-kpi-margin">
          <div className="text-[10px] font-semibold uppercase tracking-wider text-slate-400">{t("margin")}</div>
          <div className="mt-1 flex items-center gap-1 text-sm font-bold mono text-slate-900">
            <TrendingUp className="h-3.5 w-3.5 text-orange-500" />{summary.margin_percent}%
          </div>
        </div>
      </div>

      {canWrite && (
        <div className="flex flex-wrap gap-1.5" data-testid="expense-quick-chips">
          {(cfg?.settings?.expense_labels || []).map((label) => (
            <button
              key={label}
              data-testid={`quick-expense-${label.replace(/\s+/g, "-").toLowerCase()}`}
              onClick={() => setQuickAccount({ label, code: QUICK_ACCOUNTS[label] || "" })}
              className="rounded-full border border-slate-200 px-2 py-1 text-[11px] text-slate-500 transition-colors hover:border-orange-300 hover:text-orange-700"
            >
              + {label}
            </button>
          ))}
        </div>
      )}

      <ExpenseLedger deal={deal} onChanged={refresh} quickAccount={quickAccount} onQuickHandled={() => setQuickAccount(null)} />

      <div className="grid grid-cols-2 items-end gap-3">
        <div>
          <Label className="text-xs">{t("tax_percent")}</Label>
          <Input type="number" min={0} max={100} className="h-9" data-testid="pnl-tax-input" disabled={!canWrite}
            value={taxPercent} onChange={(e) => setTaxPercent(e.target.value)} />
        </div>
        <div className="text-right text-xs text-slate-500">
          <div>{t("gross_profit")}: <b className="mono text-slate-800">{formatIDR(summary.gross_profit)}</b></div>
          <div>{t("tax")}: <b className="mono text-slate-800">{formatIDR(summary.tax_amount)}</b></div>
        </div>
      </div>

      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={saveTax} disabled={saving} data-testid="save-pnl-button" className="bg-slate-900 hover:bg-slate-800">
            {t("save")}
          </Button>
        </div>
      )}
    </div>
  );
};

export default PnlPanel;
